import axios from 'axios'

class DiscoveryService {
  constructor() {
    this.isScanning = false
    this.deviceFoundCallbacks = []
    this.errorCallbacks = []
    this.progressCallbacks = []
    this.scanTimeout = 3000 // 3 seconds timeout for device testing
  }

  // Register callback for device found events
  onDeviceFound(callback) {
    this.deviceFoundCallbacks.push(callback)
  }

  // Register callback for error events
  onError(callback) {
    this.errorCallbacks.push(callback)
  }

  // Register callback for progress updates
  onProgress(callback) {
    this.progressCallbacks.push(callback)
  }

  // Emit device found event
  emitDeviceFound(device) {
    this.deviceFoundCallbacks.forEach(callback => callback(device))
  }

  // Emit error event
  emitError(error) {
    this.errorCallbacks.forEach(callback => callback(error))
  }

  // Emit progress event
  emitProgress(progress) {
    this.progressCallbacks.forEach(callback => callback(progress))
  }

  // Test if a device is responding and get device info - Simplified for manual connection
  async testDevice(ip, port = 80) {
    console.log(`🔍 Testing device at: ${ip}:${port}`)

    try {
      // Try NodeMCU.ino specific endpoints
      const endpoints = ['/status', '/discovery', '/test']

      for (const endpoint of endpoints) {
        try {
          console.log(`  Trying ${ip}:${port}${endpoint}...`)

          const response = await axios.get(`http://${ip}:${port}${endpoint}`, {
            timeout: this.scanTimeout,
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          })

          console.log(`  ✅ ${ip}:${port}${endpoint} responded with status:`, response.status)

          if (response.data) {
            const deviceData = response.data

            // Determine device type and name
            let deviceName = 'Unknown Device'
            let deviceType = 'unknown'

            // Check for EZ Control specific identifiers
            if (deviceData.name === 'EZ Control Device' ||
              deviceData.device === 'nodemcu' ||
              deviceData.ssid === 'EZ_Control_AP' ||
              deviceData.hotspot_ssid === 'EZ_Control_AP') {
              deviceType = 'nodemcu'
              deviceName = deviceData.name || 'EZ Control Device'
            } else if (deviceData.name) {
              deviceName = deviceData.name
              deviceType = deviceData.device || 'unknown'
            } else if (deviceData.device) {
              deviceName = deviceData.device
              deviceType = deviceData.device
            }

            // Additional checks for NodeMCU devices
            if (deviceData.version && deviceData.mac &&
              (deviceData.pinStates || deviceData.direction !== undefined)) {
              deviceType = 'nodemcu'
              if (!deviceName.includes('EZ Control')) {
                deviceName = 'EZ Control Device'
              }
            }

            console.log(`🎯 Found device: ${deviceName} (${deviceType}) at ${ip}:${port}`)

            return {
              ip: ip,
              port: port,
              name: deviceName,
              type: deviceType,
              version: deviceData.version || 'Unknown',
              mode: deviceData.mode || 'wifi',
              ssid: deviceData.ssid || deviceData.hotspot_ssid || 'Unknown',
              rssi: deviceData.rssi || 0,
              mac: deviceData.mac || 'Unknown',
              status: deviceData.status || 'available',
              direction: deviceData.direction || 'STOP',
              speed: deviceData.speed || 0,
              isMoving: deviceData.isMoving || false,
              pinStates: deviceData.pinStates || {},
              deviceData: deviceData,
              endpoint: endpoint,
              responseTime: Date.now()
            }
          }
        } catch (endpointError) {
          console.log(`  ❌ ${ip}:${port}${endpoint} failed:`, endpointError.message)
          continue
        }
      }

      // No device found at this IP
      console.log(`❌ No device found at ${ip}:${port}`)
      return null

    } catch (error) {
      console.log(`❌ Connection failed to ${ip}:${port}:`, error.message)
      return null
    }
  }

  // Simple manual device connection - Main method for manual IP entry
  async connectToDevice(ip, port = 80) {
    try {
      console.log(`🔗 Attempting to connect to device at ${ip}:${port}`)

      const device = await this.testDevice(ip, port)

      if (device) {
        console.log(`✅ Successfully connected to device: ${device.name}`)
        this.emitDeviceFound(device)
        return device
      } else {
        throw new Error(`No EZ Control device found at ${ip}:${port}`)
      }
    } catch (error) {
      console.error(`❌ Failed to connect to ${ip}:${port}:`, error.message)
      this.emitError(error)
      throw error
    }
  }

  // Test connection to a specific device
  async testConnection(deviceIP, port = 80) {
    try {
      console.log(`🧪 Testing connection to ${deviceIP}:${port}`)

      // Try multiple endpoints for better compatibility
      const endpoints = ['/status', '/test', '/discovery']

      for (const endpoint of endpoints) {
        try {
          const response = await axios.get(`http://${deviceIP}:${port}${endpoint}`, {
            timeout: 5000
          })
          console.log(`✅ Connection test successful via ${endpoint}`)
          return response.data
        } catch (error) {
          console.log(`❌ Endpoint ${endpoint} failed:`, error.message)
          continue
        }
      }

      throw new Error('All endpoints failed')
    } catch (error) {
      throw new Error(`Connection test failed: ${error.message}`)
    }
  }

  // Quick scan for common EZ Control IPs (router mode focused)
  async quickScan() {
    if (this.isScanning) {
      throw new Error('Scan already in progress')
    }

    this.isScanning = true
    console.log(`🚀 Starting quick scan...`)

    try {
      // Check for Chrome Extension
      if (window.__EZ_CONTROL_EXTENSION_ACTIVE__) {
        console.log('🧩 Using EZ Control Chrome Extension for scanning...')
        try {
          const devices = await this.scanWithExtension()
          console.log(`🧩 Extension found ${devices.length} devices`)

          devices.forEach(device => {
            this.emitDeviceFound(device)
          })

          return devices
        } catch (extError) {
          console.error('Extension scan failed, falling back to direct scan:', extError)
          // Fallback to direct scan below
        }
      }

      // Common IPs where EZ Control devices might be found in router mode
      const commonIPs = [
        '192.168.0.9',    // Your current device IP
        '192.168.0.100',  // Common DHCP range
        '192.168.0.101',  // Common DHCP range
        '192.168.0.102',  // Common DHCP range
        '192.168.1.100',  // Alternative network range
        '192.168.1.101',  // Alternative network range
        '192.168.1.102'   // Alternative network range
      ]

      const foundDevices = []

      for (const ip of commonIPs) {
        if (!this.isScanning) break

        console.log(`📡 Testing ${ip}...`)
        try {
          const device = await this.testDevice(ip)
          if (device) {
            foundDevices.push(device)
            console.log(`✅ Found device at ${ip}`)
          }
        } catch (error) {
          console.log(`❌ No device at ${ip}: ${error.message}`)
        }
      }

      console.log(`📊 Quick scan completed: Found ${foundDevices.length} devices`)
      return foundDevices

    } catch (error) {
      console.error('❌ Quick scan error:', error)
      this.emitError(error)
      throw error
    } finally {
      this.isScanning = false
      console.log('🏁 Quick scan completed')
    }
  }

  // Helper to scan using the extension
  async scanWithExtension() {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        window.removeEventListener('message', handler)
        reject(new Error('Extension scan timeout'))
      }, 5000)

      const handler = (event) => {
        if (event.source !== window || !event.data || event.data.type !== 'EZ_CONTROL_SCAN_RESULT') return

        clearTimeout(timeout)
        window.removeEventListener('message', handler)

        if (event.data.payload && event.data.payload.success) {
          resolve(event.data.payload.devices || [])
        } else {
          reject(new Error('Extension returned failure'))
        }
      }

      window.addEventListener('message', handler)
      window.postMessage({ type: 'SCAN_NETWORK', source: 'EZ_CONTROL_REACT' }, '*')
    })
  }

  // Stop any ongoing operations
  stopScan() {
    this.isScanning = false
  }

  // Check if device is EZ Control compatible
  isEZControlDevice(deviceData) {
    return deviceData && (
      deviceData.name === 'EZ Control Device' ||
      deviceData.device === 'nodemcu' ||
      deviceData.ssid === 'EZ_Control_AP' ||
      deviceData.hotspot_ssid === 'EZ_Control_AP' ||
      (deviceData.version && deviceData.mac && deviceData.pinStates)
    )
  }

  // Get device status for manual connection
  async getDeviceStatus(ip, port = 80) {
    try {
      const response = await axios.get(`http://${ip}:${port}/status`, {
        timeout: 5000
      })
      return response.data
    } catch (error) {
      throw new Error(`Failed to get device status: ${error.message}`)
    }
  }
}

export const discoveryService = new DiscoveryService() 